/**
 * "Text shape" for BoundedText: fits the text inside an arbitrary shape instead of the rectangle.
 *
 * The shape image is rasterized into a bitmap mask, and the layout only asks the mask which
 * horizontal spans are free along a band - so convex, concave (two spans on a band) and compound
 * shapes all go through the same code.
 */

// Long side of the rasterized mask: enough precision for line layout, cheap to scan per band
const MASK_RESOLUTION = 512;
// Alpha (or luminance, for opaque images) threshold separating inside from outside pixels
const MASK_THRESHOLD = 128;
// Steps a line advance is subdivided into when a band fits nothing: the band slides down to where
//  the shape opens up instead of losing a whole line. The last step lands on the next band anyway
const EMPTY_BAND_STEPS = 8;
const EPSILON = 1e-6;
// A mask holds ~1.3MB of pixel data plus its guide canvas, so the cache is capped. Evicting is
//  safe: an element keeps its own reference (item(0)._shapeMask), the entry is only rebuilt if the
//  shape is picked again. Far above the shapes a single template applies at once
const MASK_CACHE_SIZE = 24;

const maskCache = new Map();

/** Least recently used first, which is what makes the Map's insertion order the eviction order */
function readMaskCache(cacheKey) {
    if (!maskCache.has(cacheKey)) {
        return undefined;
    }
    const mask = maskCache.get(cacheKey);
    maskCache.delete(cacheKey);
    maskCache.set(cacheKey, mask);
    return mask;
}

function writeMaskCache(cacheKey, mask) {
    maskCache.set(cacheKey, mask);
    if (maskCache.size > MASK_CACHE_SIZE) {
        maskCache.delete(maskCache.keys().next().value);
    }
}

/**
 * Builds a mask from a flat "inside" grid, cropped to the bounds of its inside pixels so padding
 * around the shape (common in uploaded images) doesn't shrink the fit. Canvas-free so it can be
 * unit tested.
 * @param {Uint8Array|number[]} inside row-major grid of width * height, truthy = inside the shape
 * @param {number} width
 * @param {number} height
 * @returns {Object|null} mask exposing only its size and its queries, or null when no pixel is
 *  inside
 */
function buildMask(inside, width, height) {
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (inside[y * width + x]) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }
    }
    if (maxX < 0) {
        return null;
    }

    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const cropped = new Uint8Array(w * h);
    // outsidePrefix[y * w + x] = outside pixels in column x within rows [0, y), so checking whether
    //  a column is free along any band is a single subtraction
    const outsidePrefix = new Int32Array((h + 1) * w);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const isInside = inside[(y + minY) * width + x + minX] ? 1 : 0;
            cropped[y * w + x] = isInside;
            outsidePrefix[(y + 1) * w + x] = outsidePrefix[y * w + x] + 1 - isInside;
        }
    }

    let guideCanvas = null;

    // The pixels stay in this closure instead of being properties of the mask: as properties, two
    //  ~260k entry typed arrays hang off a canvas element, and every generic deep walker that
    //  reaches that element walks them index by index on every mouse move - Vue's deep watcher over
    //  the canvas (_traverse), lodash's cloneDeep in the drag and undo paths, angular.copy. Being
    //  unreachable stops all of them, where Object.freeze only stopped Vue's; a copied mask still
    //  works, since a clone keeps the same function references
    return Object.freeze({
        width: w,
        height: h,

        /**
         * Horizontal spans free along the WHOLE band [y0, y1] (box coordinates, top-left origin),
         * left to right. Rows are rounded outwards so a span never covers a pixel outside the shape.
         * @param {{scale: number, offsetX: number, offsetY: number}} fit see fitMask
         * @returns {{x: number, width: number}[]}
         */
        getBandSlots(fit, y0, y1, inset = 0, minWidth = 0) {
            const r0 = Math.floor((y0 - fit.offsetY) / fit.scale + EPSILON);
            const r1 = Math.max(Math.ceil((y1 - fit.offsetY) / fit.scale - EPSILON), r0 + 1);
            if (r0 < 0 || r1 > h) {
                return [];
            }

            const slots = [];
            let runStart = -1;
            for (let x = 0; x <= w; x++) {
                const free = x < w && outsidePrefix[r1 * w + x] - outsidePrefix[r0 * w + x] === 0;
                if (free && runStart < 0) {
                    runStart = x;
                } else if (!free && runStart >= 0) {
                    const left = fit.offsetX + runStart * fit.scale + inset;
                    const right = fit.offsetX + x * fit.scale - inset;
                    if (right > left && right - left >= minWidth) {
                        slots.push({ x: left, width: right - left });
                    }
                    runStart = -1;
                }
            }
            return slots;
        },

        /**
         * Canvas with the mask's inside pixels filled, drawn over the object as an editor-only
         * guide. It is painted with "multiply" (see BoundedText.drawBorders), so this is the color
         * the guide ends up having over a white area.
         */
        getGuideCanvas() {
            if (!guideCanvas) {
                guideCanvas = fabric.util.createCanvasElement();
                guideCanvas.width = w;
                guideCanvas.height = h;
                const ctx = guideCanvas.getContext('2d');
                const imageData = ctx.createImageData(w, h);
                for (let i = 0; i < cropped.length; i++) {
                    if (cropped[i]) {
                        imageData.data.set([0xed, 0xe9, 0xfb, 255], i * 4);
                    }
                }
                ctx.putImageData(imageData, 0, 0);
            }
            return guideCanvas;
        }
    });
}

/**
 * Where the mask lands inside a box of the given size: scaled uniformly as big as possible
 * (keeps the shape's proportions) and centered.
 */
function fitMask(mask, boxWidth, boxHeight) {
    const scale = Math.min(boxWidth / mask.width, boxHeight / mask.height);
    const width = mask.width * scale;
    const height = mask.height * scale;
    return { scale, width, height, offsetX: (boxWidth - width) / 2, offsetY: (boxHeight - height) / 2 };
}

/** @see the mask's own getBandSlots */
function getBandSlots(mask, fit, y0, y1, inset = 0, minWidth = 0) {
    return mask.getBandSlots(fit, y0, y1, inset, minWidth);
}

/**
 * Greedy word wrap into the shape: bands top to bottom, free spans left to right within each band.
 * Words are never broken - a span too narrow for the next word is skipped - and "\n" continues on
 * the next band. A band that fits nothing doesn't consume a line: it slides down in small steps
 * until the shape is wide enough, so curved tops (a heart's lobes, a circle's crown) don't lose a
 * whole line to the narrow rows above the first one that fits.
 * @param {Object} options
 * @param {string} options.text
 * @param {(line: string) => number} options.measure rendered width of a line, stroke included
 * @param {(y0: number, y1: number) => {x: number, width: number}[]} options.getSlots
 * @param {number} options.lineAdvance vertical distance between consecutive bands
 * @param {number} options.bandHeight height each band must be free along (glyph box + stroke)
 * @param {number} options.startY top of the first band
 * @param {number} options.endY bottom limit
 * @returns {{lines: {text: string, x: number, width: number, y: number, endsParagraph: boolean}[], fits: boolean, bottom: number}}
 */
function layoutTextInShape({ text, measure, getSlots, lineAdvance, bandHeight, startY, endY }) {
    const paragraphs = text
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((paragraph) => paragraph.split(' ').filter(Boolean));
    const lines = [];
    let y = startY;
    let bottom = startY;

    if (!(lineAdvance > 0)) {
        return { lines, fits: false, bottom };
    }

    for (const words of paragraphs) {
        if (!words.length) {
            // Blank line: only takes vertical space
            y += lineAdvance;
            continue;
        }

        let wordIndex = 0;
        while (wordIndex < words.length) {
            if (y + bandHeight > endY + EPSILON) {
                return { lines, fits: false, bottom };
            }

            const lineCount = lines.length;
            for (const slot of getSlots(y, y + bandHeight)) {
                if (wordIndex >= words.length) {
                    break;
                }
                let line = words[wordIndex];
                if (measure(line) > slot.width) {
                    continue;
                }
                wordIndex++;
                while (wordIndex < words.length) {
                    const candidate = line + ' ' + words[wordIndex];
                    if (measure(candidate) > slot.width) {
                        break;
                    }
                    line = candidate;
                    wordIndex++;
                }
                lines.push({ text: line, x: slot.x, width: slot.width, y, endsParagraph: false });
                bottom = y + bandHeight;
            }
            y += lines.length > lineCount ? lineAdvance : lineAdvance / EMPTY_BAND_STEPS;
        }
        lines[lines.length - 1].endsParagraph = true;
    }

    return { lines, fits: true, bottom };
}

function readPixels(width, height, draw) {
    const canvas = fabric.util.createCanvasElement();
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    draw(ctx);
    return ctx.getImageData(0, 0, width, height).data;
}

function maskFromPixels(data, width, height, isInside) {
    const inside = new Uint8Array(width * height);
    for (let i = 0; i < inside.length; i++) {
        inside[i] = isInside(data, i * 4) ? 1 : 0;
    }
    return buildMask(inside, width, height);
}

/**
 * Transparent images (PNG/SVG): opaque pixels are the shape. Opaque images (JPG): dark pixels
 * are the shape, ie. a black silhouette on a white background.
 * @param {HTMLImageElement} img loaded image
 * @param {string} cacheKey usually the image src
 * @returns {Object|null} mask
 */
function createMaskFromImage(img, cacheKey) {
    // A shape with nothing inside caches its null, so it isn't rasterized again on every pick
    const cached = readMaskCache(cacheKey);
    if (cached !== undefined) {
        return cached;
    }

    // SVGs without intrinsic size report 0
    const naturalWidth = img.naturalWidth || img.width || MASK_RESOLUTION;
    const naturalHeight = img.naturalHeight || img.height || MASK_RESOLUTION;
    const scale = MASK_RESOLUTION / Math.max(naturalWidth, naturalHeight);
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));
    const data = readPixels(width, height, (ctx) => ctx.drawImage(img, 0, 0, width, height));

    let hasTransparency = false;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) {
            hasTransparency = true;
            break;
        }
    }
    const isInside = hasTransparency
        ? (d, i) => d[i + 3] >= MASK_THRESHOLD
        : (d, i) => 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2] < MASK_THRESHOLD;

    const mask = maskFromPixels(data, width, height, isInside);
    writeMaskCache(cacheKey, mask);
    return mask;
}

/** @see the mask's own getGuideCanvas */
function getMaskGuideCanvas(mask) {
    return mask.getGuideCanvas();
}

/**
 * layoutTextInShape anchored at the center (or bottom) of [startY, endY]: uses the fewest bands the
 * text fits in and places that block centered (or bottom aligned), so the text grows outwards from
 * the anchor as lines are added. 'top' is plain layoutTextInShape.
 * @param {Object} options layoutTextInShape options plus `anchor` ('top' | 'center' | 'bottom')
 */
function layoutTextInShapeAnchored(options) {
    const { anchor, lineAdvance, bandHeight, startY, endY } = options;
    if ((anchor !== 'center' && anchor !== 'bottom') || !(lineAdvance > 0)) {
        return layoutTextInShape(options);
    }

    const maxBands = Math.floor((endY - startY - bandHeight) / lineAdvance + EPSILON) + 1;
    if (maxBands < 1) {
        return { lines: [], fits: false, bottom: startY };
    }

    const layoutBands = (bandCount) => {
        const blockHeight = (bandCount - 1) * lineAdvance + bandHeight;
        const top = anchor === 'center' ? (startY + endY - blockHeight) / 2 : endY - blockHeight;
        return layoutTextInShape({ ...options, startY: top, endY: top + blockHeight });
    };

    let result = layoutBands(maxBands);
    if (!result.fits) {
        return result;
    }
    // Fewest bands that fit - more bands only add room, so binary search
    let lo = 1;
    let hi = maxBands;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midResult = layoutBands(mid);
        if (midResult.fits) {
            hi = mid;
            result = midResult;
        } else {
            lo = mid + 1;
        }
    }
    return result;
}

module.exports = {
    buildMask,
    fitMask,
    getBandSlots,
    layoutTextInShape,
    layoutTextInShapeAnchored,
    createMaskFromImage,
    getMaskGuideCanvas
};
